import { createContext, forwardRef } from 'react';

import { LinkProps } from '@mui/material/Link';
import { alpha, createTheme, Theme } from '@mui/material/styles';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';

import '@fontsource/nunito-sans/200.css';
import '@fontsource/nunito-sans/300.css';
import '@fontsource/nunito-sans/400.css';
import '@fontsource/nunito-sans/600.css';
import '@fontsource/nunito-sans/700.css';
import '@fontsource/nunito-sans/800.css';
import '@fontsource/nunito-sans/900.css';
import '@fontsource/poppins';
import '@fontsource/poppins/200.css';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import '@fontsource/poppins/900.css';

const LinkBehavior = forwardRef<any, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props;
    // Map href (MUI) -> to (react-router)
    return <RouterLink ref={ref} to={href} {...other} />;
  }
);
LinkBehavior.displayName = 'LinkBehavior';

export const ThemeContext = createContext({
  initDarkMode: () => {},
  initLightMode: () => {},
});

export type SubjectColorKey = 'chemistry' | 'physics' | 'mathsExt1' | 'mathsExt2';

export type BaseColorKey = 'primary' | 'secondary' | 'gray';

export type PureColorKey =
  | 'turquoise'
  | 'lightTurquoise'
  | 'red'
  | 'lightRed'
  | 'orange'
  | 'lightOrange'
  | 'yellow'
  | 'lightYellow'
  | 'green'
  | 'lightGreen'
  | 'blue'
  | 'lightBlue';

export type BackgroundColorKey = 'backdrop' | 'background';

export type NotifierColorKey = 'error' | 'warning' | 'info' | 'success';

export type ColorKey =
  | BaseColorKey
  | PureColorKey
  | SubjectColorKey
  | BackgroundColorKey
  | NotifierColorKey
  | 'divider';

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
}

/* ===========================================================================
   Colour primitives — exact ramps from the Cognito Tuition design system.
   Source of truth: every semantic colour below is derived from these.
   Use 50–300 for backgrounds & tints, 400 for the brand mark, 500 for
   subject marks, 700 for emphasis, 900 for headings, 1000 for body.
   =========================================================================== */
export const ramps = {
  turquoise: {
    50: '#F2FAF9',
    100: '#E8FCF9',
    200: '#CCF9F3',
    300: '#9EEEE2',
    400: '#6EDBCB',
    500: '#37B8B0',
    600: '#269999',
    700: '#177E81',
    800: '#0F5A61',
    900: '#00414D',
    1000: '#002B33',
  },
  neutral: {
    50: '#FAFBFC',
    100: '#F5F7FA',
    200: '#EBEFF5',
    300: '#E2E7F0',
    400: '#CCD4E0',
    500: '#B5BCC7',
    600: '#9EA4AD',
    700: '#848A94',
    800: '#6D727A',
    900: '#595E66',
    1000: '#474C52',
    1100: '#36393E',
    1200: '#1E2127',
  },
  blue: {
    50: '#F5FAFC',
    100: '#E8F7FF',
    200: '#D1EEFF',
    300: '#A6DEFF',
    400: '#6DC1F2',
    500: '#43A7E0',
    600: '#2A91CC',
    700: '#1576AD',
    800: '#0F5680',
    900: '#0B3C59',
    1000: '#06293D',
  },
  green: {
    50: '#F5FAF6',
    100: '#E8FCEC',
    200: '#CCF9D5',
    300: '#A7EEB5',
    400: '#83DB95',
    500: '#5EB870',
    600: '#4D995C',
    700: '#368044',
    800: '#276133',
    900: '#1A4923',
    1000: '#123318',
  },
  yellow: {
    50: '#FCFBF7',
    100: '#FFF9EB',
    200: '#FFF4D9',
    300: '#FFE3A3',
    400: '#FFD166',
    500: '#DBA542',
    600: '#BD8B2D',
    700: '#966A21',
    800: '#6B4B1B',
    900: '#523710',
    1000: '#422C0A',
  },
  orange: {
    50: '#FCF8F5',
    100: '#FFF3EB',
    200: '#FFEBD9',
    300: '#FFCDA3',
    400: '#FFAA66',
    500: '#EB9046',
    600: '#D37F3B',
    700: '#A55E24',
    800: '#733F15',
    900: '#593110',
    1000: '#40230B',
  },
  red: {
    50: '#FCF7F7',
    100: '#FFEBEB',
    200: '#FFD9D9',
    300: '#FFC2C2',
    400: '#FF8A8A',
    500: '#F07070',
    600: '#E55757',
    700: '#C13E3E',
    800: '#993131',
    900: '#7A2727',
    1000: '#571C1C',
  },
} as const;

const WHITE = '#FFFFFF';
const DARK_TEXT = 'rgba(0,0,0,0.87)';

const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000] as const;

type Ramp = Record<(typeof STEPS)[number], string>;

// Build a MUI colour object from a ramp, with light/dark two steps either side
// of the chosen main step (clamped to the ends of the ramp).
const band = (ramp: Ramp, mainStep: (typeof STEPS)[number], contrastText: string) => {
  const i = STEPS.indexOf(mainStep);
  const lightStep = STEPS[Math.max(0, i - 2)];
  const darkStep = STEPS[Math.min(STEPS.length - 1, i + 2)];
  return {
    light: ramp[lightStep],
    main: ramp[mainStep],
    dark: ramp[darkStep],
    contrastText,
  };
};

// Subject, status and pure-colour accents read the same in both themes — they
// sit on neutral surfaces, so only the brand/surface tokens flip per mode.
const accents = {
  // Subject accents (per the design system's subject theming)
  chemistry: band(ramps.blue, 500, WHITE),
  physics: band(ramps.green, 500, WHITE),
  mathsExt1: band(ramps.orange, 500, WHITE),
  mathsExt2: band(ramps.red, 500, WHITE),

  // Status
  error: band(ramps.red, 600, WHITE),
  warning: band(ramps.yellow, 500, DARK_TEXT),
  info: band(ramps.blue, 500, WHITE),
  success: band(ramps.green, 500, WHITE),

  // Pure colour ramps exposed as named keys (dark shades come from `.dark`)
  turquoise: band(ramps.turquoise, 400, DARK_TEXT),
  lightTurquoise: band(ramps.turquoise, 200, DARK_TEXT),
  red: band(ramps.red, 500, WHITE),
  lightRed: band(ramps.red, 300, DARK_TEXT),
  orange: band(ramps.orange, 500, WHITE),
  lightOrange: band(ramps.orange, 300, DARK_TEXT),
  yellow: band(ramps.yellow, 400, DARK_TEXT),
  lightYellow: band(ramps.yellow, 300, DARK_TEXT),
  green: band(ramps.green, 500, WHITE),
  lightGreen: band(ramps.green, 300, DARK_TEXT),
  blue: band(ramps.blue, 500, WHITE),
  lightBlue: band(ramps.blue, 300, DARK_TEXT),
};

/**
 * Ink for text and glyphs over a band's `light` tint. Every band used this way
 * holds that step pale in both themes — the accents identically, `gray` a shade
 * apart — so the ink over them has to be static too: a themed body colour turns
 * near-white against them in dark mode.
 *
 * Not `contrastText`, which belongs to `main`: MUI hovers a contained Button to
 * `dark`, so a band's own label has to survive that step, and no single ink
 * clears a `light`-to-`dark` spread. Not for `backdrop` either, whose `light`
 * inverts between modes — that one takes `backdrop.contrastText`.
 *
 * `theme.test.ts` holds every band paired with this ink above AA.
 */
export const TINT_INK = ramps.neutral[1200];

// Soft, low-spread elevation scale from the design system — never harsh black
// drops. MUI requires exactly 25 entries (index 0 = none → 24 = highest).
const lightShadows = [
  'none',
  '0 1px 2px rgba(30,33,39,0.04)',
  '0 2px 6px rgba(30,33,39,0.05), 0 1px 2px rgba(30,33,39,0.04)',
  '0 6px 16px rgba(30,33,39,0.07), 0 2px 4px rgba(30,33,39,0.04)',
  '0 14px 32px rgba(30,33,39,0.09), 0 4px 8px rgba(30,33,39,0.05)',
  ...Array(20).fill('0 24px 56px rgba(30,33,39,0.12), 0 8px 16px rgba(30,33,39,0.06)'),
];

const darkShadows = [
  'none',
  '0 1px 2px rgba(0,0,0,0.5)',
  '0 2px 6px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
  '0 6px 16px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)',
  '0 14px 32px rgba(0,0,0,0.55), 0 4px 8px rgba(0,0,0,0.3)',
  ...Array(20).fill('0 24px 56px rgba(0,0,0,0.6), 0 8px 16px rgba(0,0,0,0.35)'),
];

const lightTheme: any = {
  shadows: lightShadows,
  palette: {
    mode: ThemeMode.Light,
    // Primary is the brand teal — one ramp step lighter than the deepest emphasis
    // shades so it reads as a vivid brand mark rather than a murky dark teal.
    primary: {
      light: ramps.turquoise[300],
      main: ramps.turquoise[600],
      dark: ramps.turquoise[900],
      contrastText: WHITE,
    },
    // Secondary is the deepest teal — the default heading colour.
    secondary: {
      light: ramps.turquoise[700],
      main: ramps.turquoise[900],
      dark: ramps.turquoise[1000],
      contrastText: WHITE,
    },
    ...accents,
    // Sunken surface: one step below the neutral-100 page so inputs, table
    // stripes, and tab pills read against both the page and white cards.
    backdrop: {
      light: WHITE,
      main: ramps.neutral[200],
      dark: ramps.neutral[300],
      contrastText: DARK_TEXT,
    },
    gray: band(ramps.neutral, 800, WHITE),
    text: {
      primary: ramps.neutral[1200],
      secondary: ramps.neutral[1000],
      disabled: ramps.neutral[600],
    },
    // Foreground (icon) action colours: solid greys, not MUI's default alpha
    // rgba. Stroke-based icons (e.g. Tabler outlines) paint multiple paths
    // with `stroke="currentColor"`; with an rgba currentColor, overlapping
    // strokes compound into darker pixels and the joins read as seams.
    // hover/selected/focus/disabledBackground stay at MUI defaults — those
    // are surface tints that need alpha to blend with their background.
    action: {
      active: ramps.neutral[800],
      disabled: ramps.neutral[500],
    },
    background: {
      default: ramps.neutral[100],
      paper: WHITE,
      // Content panel surface — the brightest surface in light mode (white).
      canvas: WHITE,
    },
    divider: ramps.neutral[200],
  },
};

const darkTheme: any = {
  shadows: darkShadows,
  palette: {
    mode: ThemeMode.Dark,
    primary: {
      light: ramps.turquoise[300],
      main: ramps.turquoise[400],
      dark: ramps.turquoise[300],
      contrastText: ramps.turquoise[1000],
    },
    secondary: {
      light: ramps.turquoise[300],
      main: ramps.turquoise[400],
      dark: ramps.turquoise[300],
      contrastText: ramps.turquoise[1000],
    },
    ...accents,
    // Sunken surface: darker than the neutral-1200 page; lighter `.light` matches
    // the elevated paper so striped surfaces stay legible in dark mode.
    backdrop: {
      light: '#2A2D33',
      main: '#15171C',
      dark: '#101216',
      contrastText: ramps.neutral[100],
    },
    gray: {
      light: ramps.neutral[700],
      main: ramps.neutral[500],
      dark: ramps.neutral[300],
      contrastText: ramps.neutral[1200],
    },
    text: {
      primary: ramps.neutral[100],
      secondary: ramps.neutral[400],
      disabled: ramps.neutral[700],
    },
    // See light-mode action comment — same rationale, dark-mode weights.
    action: {
      active: WHITE,
      disabled: ramps.neutral[900],
    },
    background: {
      default: ramps.neutral[1200],
      paper: '#2A2D33',
      // Content panel surface — the darkest surface in dark mode (near-black),
      // deliberately darker than the frame so it mirrors the white panel in light mode.
      canvas: '#0C0E12',
    },
    divider: '#2E3138',
  },
};

const baseTheme: any = {
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiPaper: {
      styleOverrides: {
        // The design system uses flat surfaces — no MUI dark elevation tint.
        root: { backgroundImage: 'none' },
      },
    },
    // Cards and drawers are flat: separation comes from borders and surface
    // contrast, not elevation. Defaulting elevation to 0 also flattens the
    // temporary (modal) Drawer variant, which would otherwise lift to shadows[16].
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiDrawer: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiChip: {
      styleOverrides: {
        // Centring the label's box centres the font's ascent/descent band rather
        // than the text, so the glyphs sit high; trimming the box to the cap band
        // centres them at any size or engine. The padding then holds the
        // descenders against the label's overflow clip — a `padding` shorthand on
        // this slot elsewhere drops it — and the trimmed box no longer stretches
        // for a tall inline icon. Never position or transform the label: that
        // paints it over the delete icon's click target.
        label: {
          '@supports (text-box-trim: trim-both)': {
            textBoxTrim: 'trim-both',
            textBoxEdge: 'cap alphabetic',
            paddingBlock: 'calc((1lh - 1cap) / 2)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        // The Switch thumb defaults to shadows[1] for elevation, but the design
        // shadow scale is too soft to separate a white thumb from a white card.
        // Give the thumb its own defined elevation so it stays visible.
        thumb: {
          boxShadow:
            '0 1px 3px rgba(0,0,0,0.3), 0 1px 1px rgba(0,0,0,0.16), 0 2px 1px -1px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
      styleOverrides: {
        // Skeletons sit on four different surfaces (canvas, paper, default,
        // backdrop), so the fill is a translucent ink tint rather than a solid
        // ramp step — it keeps the same contrast against all of them. The
        // custom property lets a SkeletonGroup retune the whole subtree for a
        // surface the ink tint can't read against, such as the brand gradient.
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: `var(--skeleton-fill, ${
            isDark(theme) ? alpha(ramps.neutral[100], 0.09) : alpha(ramps.neutral[1200], 0.08)
          })`,
        }),
        text: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.shape.borderRadius / 2,
        }),
        // The design system has no square corners; rectangular placeholders
        // round to the same radius as the elements they stand in for.
        rectangular: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
        wave: ({ theme }: { theme: Theme }) => ({
          '&::after': {
            // MUI's default wave holds for 0.5s before the first sweep, which
            // reads as a frozen placeholder on fast loads.
            animationDuration: '1.6s',
            animationDelay: '0s',
            background: `linear-gradient(90deg, transparent, var(--skeleton-sheen, ${
              isDark(theme) ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.7)'
            }), transparent)`,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&::after': {
              animation: 'none',
              display: 'none',
            },
          },
        }),
        pulse: {
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        },
      },
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
    drawerWidth: 240,
    drawerWidthRail: 64,
    appbarHeight: 64,
  },
  typography: {
    fontSize: 14,
    fontFamily: '"Nunito Sans", "Helvetica", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    htmlFontSize: 16,
    h1: {
      fontSize: '2.5rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontWeightBold: 600,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontWeightBold: 600,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontSize: '1.625rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.375rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontSize: '1.125rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.25,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: '1rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.75,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.55,
      letterSpacing: '0em',
    },
    button: {
      fontSize: '0.875rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 600,
      lineHeight: 1.75,
      letterSpacing: '0em',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0em',
    },
    overline: {
      fontSize: '0.6875rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
};

export const isDark = (theme: any) => theme?.palette?.mode === ThemeMode.Dark;

/**
 * Drop shadow for a floating Paper. A `filter`, so it traces whatever shape the
 * paper paints rather than its box. The drop is heavier in dark mode: an ink
 * shadow separates far less against a near-black canvas than against a white
 * page, the same reason `darkShadows` runs deeper than `lightShadows`.
 */
export const floatingShadow = (theme: Theme) =>
  `drop-shadow(0px 2px 8px ${alpha('#000000', isDark(theme) ? 0.56 : 0.32)})`;

export const getThemeObject = (mode: ThemeMode | undefined) => ({
  ...baseTheme,
  ...(mode === ThemeMode.Dark ? darkTheme : lightTheme),
});

export const getTheme = (mode: ThemeMode | undefined) => createTheme(getThemeObject(mode));

export default getTheme;
